import { useState } from "react";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { parseStripeShopProductList } from "./stripeShopTypes";

const PAGE_SIZE = 25;

/**
 * Searches the shop-products proxy_read type for the given Stripe account. Debounces the search
 * input and resets pagination whenever the search term changes, since cursors don't carry over
 * across a changed filter.
 */
export const useShopStripeProducts = (stripeKey: string | null) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Stripe",
      type: "shop-products",
      query: JSON.stringify({ stripeKey, search: debouncedSearch || undefined, limit: PAGE_SIZE, cursor }),
    },
    enabled: !!stripeKey,
    select: (response) => parseStripeShopProductList(response.data),
  });

  const setSearchAndResetCursor = (value: string) => {
    setSearch(value);
    setCursor(undefined);
  };

  return {
    products: query.data?.data ?? [],
    hasMore: query.data?.hasMore ?? false,
    isLoading: query.isLoading,
    error: query.error,
    search,
    setSearch: setSearchAndResetCursor,
    loadMore: () => setCursor(query.data?.nextCursor),
    canLoadMore: !!query.data?.nextCursor,
  };
};
